/**
 * Master data (articles, storages, locations) can be edited at the same time on several devices.
 * Per field the later intention wins: what counts is when someone made the change, not when it
 * arrived. Equal times break by the id of the change. Server and device merge the same way.
 */

export type EntityType = "article" | "lager" | "location" | "inventory" | "card" | "import" | "recipe" | "inbound" | "reservation";

export interface EntityChange {
  /** Id of the change (UUIDv7), also used for idempotency. */
  op: string;
  type: EntityType;
  id: string;
  /** When the change was made (device time, capped by the server). */
  at: number;
  fields: Record<string, unknown>;
}

export interface Entity {
  type: EntityType;
  id: string;
  fields: Record<string, unknown>;
  /** Per field: [time, change id] of the change that set it. */
  clocks: Record<string, [number, string]>;
}

function later(a: [number, string], b: [number, string]): boolean {
  return a[0] > b[0] || (a[0] === b[0] && a[1] > b[1]);
}

/** Applies a change; returns the entity unchanged (same object) if nothing in it is newer. */
export function mergeEntity(entity: Entity | undefined, change: EntityChange): Entity {
  const base: Entity = entity ?? { type: change.type, id: change.id, fields: {}, clocks: {} };
  let next: Entity | undefined;
  const stamp: [number, string] = [change.at, change.op];
  for (const [field, value] of Object.entries(change.fields)) {
    const clock = base.clocks[field];
    if (clock && !later(stamp, clock)) continue;
    next ??= { type: base.type, id: base.id, fields: { ...base.fields }, clocks: { ...base.clocks } };
    next.fields[field] = value;
    next.clocks[field] = stamp;
  }
  return next ?? base;
}
